import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { TodoIntegration, TodoIntegrationType } from '@demind-inc/core';
import { Add, ArrowDropDown } from '@mui/icons-material';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { SIDEBAR_ICONS } from '../constants';
import './TodoList.scss';
import { ConnectTodosDropdown } from '../../TodoIntegration';
import { AddProjectDialog } from '../../Project';
import { useAddProject, useDropdownState, useStartTodoAuth } from '../../../hooks';
import { TodoListTitle } from './TodoListTitle';
import { isAppleRemindersDesktopSupported } from '../../../helpers';
import {
  useAuthContext,
  useRemoveTodoIntegration,
  useSetTodoProjectVisibility,
  useSyncTodoProjects,
  useSyncTodosApi,
  useTodoTasksContext,
  isSupportedTodoProvider,
  allTypeProjectFilter,
  projectFilterAtom,
  useDeleteTodoProject,
  generalSnackBarAtom,
} from '../../../data-access';
import { BasicDialog } from '../../common/BasicDialog';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { TodoListProject } from './TodoListProject';
import { ReactComponent as TaskViewIcon } from '../../../assets/icons/task-view.svg';
import { useTranslation } from 'react-i18next';

interface TodoListProps {
  className?: string;
}

const TodoList: React.FC<TodoListProps> = ({ className }) => {
  const { t } = useTranslation();
  const [disconnectingTodoType, setDisconnectingTodoType] = useState<TodoIntegrationType | null>(
    null
  );
  const [syncingTodoType, setSyncingTargetType] = useState<TodoIntegrationType | null>(null);
  const [selectedProjectFilters, setSelectedProjectFilters] = useRecoilState(projectFilterAtom);
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);
  const isInboxProject = (name: string) => name.trim() === 'Inbox';
  const providerDisablesDelete = (source: TodoIntegrationType) =>
    source === 'github' || (source === 'apple-reminders' && !isAppleRemindersDesktopSupported());

  const { anchorEl, handleCloseDropdown, handleOpenDropdown } = useDropdownState();
  const { user } = useAuthContext();
  const { connectedTodos, handleOpenTodoAuth, generatingTodoAuth } = useStartTodoAuth({});
  const { removeTodoIntegration } = useRemoveTodoIntegration();
  const { syncTodoProjects } = useSyncTodoProjects();
  const { todoProjects } = useTodoTasksContext();
  const { reSyncTasks } = useSyncTodosApi();
  const { setTodoProjectVisibility } = useSetTodoProjectVisibility();
  const { projectDialogVisible, isAddingProject, handleAddProject, handleProjectDialogVisible } =
    useAddProject();
  const { deleteTodoProject, isPending: isDeletingProject } = useDeleteTodoProject();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; name: string } | null>(null);

  const hanelDeleteIntegration = async (targetTodo: TodoIntegration) => {
    setDisconnectingTodoType(targetTodo.source);

    const hasProjectFromDisconnectedIntegration = selectedProjectFilters.some((filter) => {
      const project = todoProjects.find((p) => p.todoTaskId === filter.todoTaskId);
      return project?.appFrom === targetTodo.source;
    });

    if (hasProjectFromDisconnectedIntegration) {
      setSelectedProjectFilters([allTypeProjectFilter]);
    }

    await removeTodoIntegration({
      userId: user.userId,
      todoIntegrationId: targetTodo.todoIntegrationId,
    });
    setDisconnectingTodoType(null);
  };

  const hanelSyncIntegration = async (targetTodo: TodoIntegration) => {
    if (['notion', 'trello', 'github', 'clickup', 'google-tasks'].includes(targetTodo.source)) {
      await handleOpenTodoAuth(targetTodo.source);
      return;
    }

    setSyncingTargetType(targetTodo.source);

    if (targetTodo.source === 'apple-reminders') {
      await reSyncTasks('apple-reminders');
      setSyncingTargetType(null);
      return;
    }

    let targetOriginalBoardIds = undefined;
    const newProjectIds = await syncTodoProjects({
      userId: user.userId,
      todoType: targetTodo.source,
      targetOriginalBoardIds,
    });
    await reSyncTasks(targetTodo.source, newProjectIds, true);
    setSyncingTargetType(null);
  };

  const lifestackProjects = todoProjects.filter((p) => p.appFrom === 'lifestack');

  const handleDeleteProject = (project: { todoTaskId: string; name: string }) => {
    if (isInboxProject(project.name)) {
      return;
    }
    setProjectToDelete({ id: project.todoTaskId, name: project.name });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) {
      return;
    }

    const hasProjectInFilters = selectedProjectFilters.some(
      (filter) => filter.todoTaskId === projectToDelete.id
    );

    const appFrom = todoProjects.find((p) => p.todoTaskId === projectToDelete.id)?.appFrom;

    try {
      await deleteTodoProject({
        userId: user.userId,
        boardId: projectToDelete.id,
      });

      if (hasProjectInFilters) {
        setSelectedProjectFilters([allTypeProjectFilter]);
      }
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    } catch (error: any) {
      setGeneralSnackbar(t('task.project.deleteProjectFailedProvider', { provider: appFrom }));
      setTimeout(() => setGeneralSnackbar(null), 6000);
    }
  };

  return (
    <>
      <div className={clsx('todos', className)}>
        {lifestackProjects.length > 0 && (
          <Accordion className="todos__accordion" elevation={0} defaultExpanded>
            <AccordionSummary expandIcon={<ArrowDropDown className="todos__more__icon" />}>
              <div className="todo-list-title" style={{ height: '32px' }}>
                <TaskViewIcon className="todo-list-title__icon" />
                <div className="todo-list-title__text">Lifestack</div>
              </div>
            </AccordionSummary>
            <AccordionDetails className="todos__accordion__projects">
              {lifestackProjects.map((project) => {
                const canDelete = !isInboxProject(project.name);
                return (
                  <TodoListProject
                    key={project.todoTaskId}
                    project={project}
                    onSelect={(selected) =>
                      setTodoProjectVisibility({ boardId: project.todoTaskId, closed: !selected })
                    }
                    defaultSelected={!project.closed}
                    canDelete={canDelete}
                    onDelete={() => handleDeleteProject(project)}
                  />
                );
              })}
              <button
                type="button"
                className="todos__button todos__button--add-project"
                onClick={() => handleProjectDialogVisible(true)}
              >
                <div className="todos__button__icon">
                  <Add />
                </div>
                <div className="todos__button__text">{t('task.project.addProject')}</div>
              </button>
            </AccordionDetails>
          </Accordion>
        )}

        {connectedTodos.map((targetTodo) => (
          <Accordion
            key={targetTodo.todoIntegrationId}
            className="todos__accordion"
            elevation={0}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ArrowDropDown className="todos__more__icon" />}>
              <TodoListTitle
                key={targetTodo.source}
                iconSource={SIDEBAR_ICONS[targetTodo.source]}
                todoType={targetTodo.source}
                isDisconnecting={disconnectingTodoType === targetTodo.source}
                isSyncing={syncingTodoType === targetTodo.source}
                onSyncIntegration={() => hanelSyncIntegration(targetTodo)}
                onDeleteIntegration={() => hanelDeleteIntegration(targetTodo)}
              />
            </AccordionSummary>
            <AccordionDetails className="todos__accordion__projects">
              {todoProjects
                .filter((p) => p.appFrom === targetTodo.source)
                .map((project) => {
                  const canDelete =
                    !providerDisablesDelete(targetTodo.source) && !isInboxProject(project.name);
                  return (
                    <TodoListProject
                      key={project.todoTaskId}
                      project={project}
                      onSelect={(selected) =>
                        setTodoProjectVisibility({ boardId: project.todoTaskId, closed: !selected })
                      }
                      defaultSelected={!project.closed}
                      canDelete={canDelete}
                      onDelete={() => handleDeleteProject(project)}
                    />
                  );
                })}
            </AccordionDetails>
          </Accordion>
        ))}
        <button
          aria-label={t('task.todoIntegration.addTodos')}
          onClick={handleOpenDropdown}
          className="todos__button"
        >
          <div className="todos__button__icon">
            <Add />
          </div>
          <div className="todos__button__text">{t('task.todoIntegration.todos')}</div>
        </button>
      </div>
      <ConnectTodosDropdown
        anchorEl={anchorEl}
        onClose={handleCloseDropdown}
        onClickItem={handleOpenTodoAuth}
        generatingTodoAuth={generatingTodoAuth}
        connectedTodos={connectedTodos}
      />
      <AddProjectDialog
        isAdding={isAddingProject}
        visible={projectDialogVisible}
        existingProjectNames={todoProjects.map((p) => p.name)}
        onAdd={handleAddProject}
        onClose={() => handleProjectDialogVisible(false)}
      />
      <BasicDialog
        visible={deleteConfirmOpen}
        title={t('task.project.deleteProject')}
        description={t('task.project.deleteProjectDescription', {
          name: projectToDelete?.name,
        })}
        ctaString={t('task.common.delete')}
        isLoading={isDeletingProject}
        onYes={handleConfirmDelete}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setProjectToDelete(null);
        }}
      />
    </>
  );
};

export default TodoList;
